import { useEffect, useRef } from "react";

const MOBILE_BREAKPOINT = Number(process.env.MOBILE_BREAKPOINT) || 980;

const clearBodyScrollLock = (scrollY = 0) => {
  const { body, documentElement } = document;
  delete body.dataset.searchOverlayOpen;
  documentElement.style.removeProperty("overflow");
  body.style.removeProperty("overflow");
  body.style.removeProperty("position");
  body.style.removeProperty("top");
  body.style.removeProperty("left");
  body.style.removeProperty("right");
  body.style.removeProperty("width");
  window.scrollTo(0, scrollY);
};

const useSearchOverlayScrollLock = ({
  isMobile,
  variant,
  isOverlayOpen,
  onCloseOverlay,
}: {
  isMobile: boolean;
  variant: "default" | "header";
  isOverlayOpen: boolean;
  onCloseOverlay: () => void;
}): void => {
  const lockedScrollYRef = useRef(0);

  useEffect(() => {
    if (!(isMobile && variant === "header" && isOverlayOpen)) {
      return undefined;
    }

    const { body, documentElement } = document;
    const previousDatasetValue = body.dataset.searchOverlayOpen;
    const shouldLockPageScroll = body.style.overflow !== "hidden";

    body.dataset.searchOverlayOpen = "true";

    if (shouldLockPageScroll) {
      lockedScrollYRef.current = window.scrollY;
      documentElement.style.overflow = "hidden";
      body.style.overflow = "hidden";
      body.style.position = "fixed";
      body.style.top = `-${lockedScrollYRef.current}px`;
      body.style.left = "0";
      body.style.right = "0";
      body.style.width = "100%";
    }

    return () => {
      if (previousDatasetValue === undefined) {
        delete body.dataset.searchOverlayOpen;
      } else {
        body.dataset.searchOverlayOpen = previousDatasetValue;
      }

      if (shouldLockPageScroll) {
        documentElement.style.removeProperty("overflow");
        body.style.removeProperty("overflow");
        body.style.removeProperty("position");
        body.style.removeProperty("top");
        body.style.removeProperty("left");
        body.style.removeProperty("right");
        body.style.removeProperty("width");
        window.scrollTo(0, lockedScrollYRef.current);
      }
    };
  }, [isMobile, variant, isOverlayOpen]);

  useEffect(() => {
    if (variant !== "header") {
      return undefined;
    }

    const restoreScroll = () => {
      clearBodyScrollLock(lockedScrollYRef.current);
    };

    const handleResize = () => {
      if (window.innerWidth < MOBILE_BREAKPOINT) {
        return;
      }
      restoreScroll();
      if (isOverlayOpen) {
        onCloseOverlay();
      }
    };

    if (window.innerWidth >= MOBILE_BREAKPOINT) {
      restoreScroll();
      if (isOverlayOpen) {
        onCloseOverlay();
      }
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [variant, isOverlayOpen, onCloseOverlay]);
};

export default useSearchOverlayScrollLock;
